/**
 * ScientificExamLayout.tsx
 * Scientific, organized exam interface with modern UX patterns.
 */

import { ReactNode, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Clock,
  AlertTriangle,
  Menu,
  Save,
  Send,
  Bell,
  User,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@vsat/shared/components';
import { Avatar, AvatarFallback, AvatarImage } from '@vsat/shared/components';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@vsat/shared/components';
import { cn } from '@vsat/shared/lib/utils';
import { useAuthStore } from '@vsat/shared/store/authStore';
import { getLocalizedSubject } from '@/utils/subjectLocalization';
import ScientificSidebar from './ScientificSidebar';
import ExamFooter from './ExamFooter';
import ProgressPanel from './ProgressPanel';
import NavigationFooter from './NavigationFooter';
import type { DemoQuestion, DemoAnswerMap } from '../types';

interface TimerDisplayProps {
  remaining: number;
  className?: string;
}

function TimerDisplay({ remaining, className = '' }: TimerDisplayProps) {
  const minutes = Math.floor(remaining / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);

  const getTimerStyle = () => {
    if (remaining <= 60000) return 'bg-red-100 text-red-700 border-red-300 animate-pulse';
    if (remaining <= 300000) return 'bg-amber-100 text-amber-700 border-amber-300';
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-1.5 rounded-full border-2 transition-all',
        getTimerStyle(),
        className
      )}
    >
      <Clock className='w-4 h-4' />
      <span className='font-mono font-bold text-sm'>
        {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
      </span>
      {remaining <= 300000 && <AlertTriangle className='w-4 h-4 ml-1' />}
    </div>
  );
}

interface ExamLayoutInfo {
  id?: string;
  title?: string;
  subject?: string;
  totalQuestions?: number;
  totalPoints?: number;
}

interface Props {
  children: ReactNode;
  questions: DemoQuestion[];
  answers: DemoAnswerMap;
  currentIndex: number;
  timeRemaining: number;
  zoom: number;
  securityLevel?: 'normal' | 'warning' | 'restricted';
  violationCount?: number;
  maxViolations?: number;
  isTabActive?: boolean;
  onJumpToQuestion: (index: number) => void;
  onZoomChange: (zoom: number) => void;
  onSubmit: () => void;
  onExit: () => void;
  examInfo?: ExamLayoutInfo;
}

export default function ScientificExamLayout({
  children,
  questions,
  answers,
  currentIndex,
  timeRemaining,
  zoom,
  securityLevel = 'normal',
  violationCount = 0,
  maxViolations = 3,
  isTabActive = true,
  onJumpToQuestion,
  onZoomChange,
  onSubmit,
  onExit,
  examInfo,
}: Props) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [isMobile, setIsMobile] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  // Function to get initials from full name (consistent with other components)
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1280);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const currentQuestion = questions[currentIndex];
  const fallbackSubject = currentQuestion?.subject || 'Tổng hợp';
  const localizedSubject = getLocalizedSubject(examInfo?.subject || fallbackSubject, t);
  const questionTypeLabel =
    currentQuestion?.type === 'cluster'
      ? t('landing.exam.layout.clusterQuestion', { defaultValue: 'Câu hỏi tình huống' })
      : currentQuestion?.type === 'trueFalse'
        ? t('landing.exam.layout.trueFalseQuestion', { defaultValue: 'Đúng/Sai' })
        : currentQuestion?.type === 'matching'
          ? t('landing.exam.layout.matchingQuestion', { defaultValue: 'Ghép đôi' })
          : t('landing.exam.layout.multipleChoice', { defaultValue: 'Trắc nghiệm' });
  const headerTitle = examInfo?.title || `${localizedSubject} - ${questionTypeLabel}`;
  const headerSubtitle = examInfo?.title ? `${localizedSubject} • ${questionTypeLabel}` : undefined;
  const answeredCount = Object.keys(answers).filter(id => {
    const answer = answers[id];
    return answer?.value !== undefined && answer?.value !== null && answer?.value !== '';
  }).length;
  const unansweredCount = questions.length - answeredCount;
  const timeRemainingStr = `${Math.floor(timeRemaining / 60000)}:${String(Math.floor((timeRemaining % 60000) / 1000)).padStart(2, '0')}`;
  const isTimeWarning = timeRemaining <= 600000;

  const handleSaveDraft = () => {
    console.log('Saving draft...');
  };

  const handleSubmit = () => {
    setShowSubmitConfirm(true);
  };

  const confirmSubmit = async () => {
    console.log('confirmSubmit called');
    try {
      // Close modal first
      setShowSubmitConfirm(false);
      // Call onSubmit - it may be async, so we handle it properly
      console.log('Calling onSubmit callback');

      // Call onSubmit (it returns void, so no need to await)
      onSubmit();

      console.log('onSubmit callback completed');
    } catch (error) {
      console.error('Error in confirmSubmit:', error);
      // Ensure modal closes even on error
      setShowSubmitConfirm(false);
      // Re-open modal to show error if needed
      // setShowSubmitConfirm(true);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      onJumpToQuestion(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      onJumpToQuestion(currentIndex + 1);
    }
  };

  return (
    <div className='h-screen flex flex-col overflow-hidden dark:from-gray-900 dark:via-primary/900/20 dark:to-primary/900/20'>
      {/* Optimized Header */}
      <header className='sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-border shadow-sm'>
        <div className='flex items-center justify-between h-12 lg:h-14 px-3 lg:px-6'>
          {/* Left Side - Logo & Title */}
          <div className='flex items-center gap-3 min-w-0 flex-1'>
            <div className='w-7 h-7 bg-gradient-to-br from-primary to-primary/80 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary/25'>
              <span className='text-white font-bold text-sm'>F</span>
            </div>
            <div className='min-w-0'>
              <h1 className='text-sm lg:text-base font-semibold text-foreground truncate'>
                {headerTitle}
              </h1>
              {headerSubtitle && (
                <p className='text-[11px] lg:text-xs text-muted-foreground truncate'>
                  {headerSubtitle}
                </p>
              )}
            </div>
          </div>

          {/* Right Side - Actions & Timer */}
          <div className='flex items-center gap-2 lg:gap-3 flex-shrink-0'>
            <Button
              variant='ghost'
              size='sm'
              onClick={handleSaveDraft}
              className='hidden sm:flex text-muted-foreground hover:text-primary  hover:bg-primary/10 dark:hover:bg-primary/20/10 text-xs h-8 px-3 transition-all duration-300'
            >
              <Save className='w-3 h-3 mr-1.5' />
              {t('landing.exam.layout.save')}
            </Button>

            <Button
              size='sm'
              onClick={handleSubmit}
              className='bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-white font-medium px-4 py-1.5 text-xs lg:text-sm h-8 shadow-lg shadow-primary/25 transition-all duration-300'
            >
              <Send className='w-3 h-3 mr-1.5' />
              {t('landing.exam.layout.submit')}
            </Button>

            <div
              className={cn(
                'text-sm font-mono font-bold px-3 py-1.5 rounded-md border h-8 flex items-center',
                isTimeWarning
                  ? 'text-red-600 bg-red-50 border-red-300 animate-pulse'
                  : 'text-foreground bg-gray-50 dark:bg-gray-800 border-border'
              )}
              data-tour='top-timer'
            >
              {timeRemainingStr}
            </div>

            <Avatar className='w-7 h-7 ring-2 ring-primary/20'>
              <AvatarImage src={user?.avatar} alt={user?.name || 'User'} />
              <AvatarFallback className='bg-gradient-to-br from-primary to-primary/80 text-white text-xs'>
                {getInitials(user?.name || 'User')}
              </AvatarFallback>
            </Avatar>
          </div>
        </div>
      </header>

      {/* Mobile Progress Toggle */}
      {isMobile && (
        <div className='flex-shrink-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-border px-3 py-2'>
          <div className='flex items-center justify-between'>
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant='ghost'
                  size='sm'
                  className='gap-2 h-8 px-3 text-xs hover:bg-primary/10 dark:hover:bg-primary/20/10 hover:text-primary  transition-all duration-300'
                >
                  <Menu className='w-3 h-3' />
                  {t('landing.exam.layout.progress')}
                </Button>
              </SheetTrigger>
              <SheetContent side='left' className='w-72 p-0'>
                <SheetHeader className='p-3 border-b border-border'>
                  <SheetTitle className='text-sm text-foreground'>
                    {t('landing.exam.layout.examProgress')}
                  </SheetTitle>
                </SheetHeader>
                <ProgressPanel
                  questions={questions}
                  answers={answers}
                  currentIndex={currentIndex}
                  timeRemaining={timeRemainingStr}
                  onQuestionSelect={onJumpToQuestion}
                />
              </SheetContent>
            </Sheet>

            <div className='text-xs text-muted-foreground'>
              {t('landing.exam.layout.questionProgress', {
                current: currentIndex + 1,
                total: questions.length,
                answered: answeredCount,
                time: timeRemainingStr,
              })}
            </div>
          </div>
        </div>
      )}

      <div className='flex flex-1 min-h-0 overflow-hidden'>
        {/* Progress Panel - Desktop */}
        {!isMobile && (
          <ProgressPanel
            questions={questions}
            answers={answers}
            currentIndex={currentIndex}
            timeRemaining={timeRemainingStr}
            onQuestionSelect={onJumpToQuestion}
          />
        )}

        {/* Content Area */}
        <div className='flex-1 min-h-0 overflow-y-auto'>
          <div className='max-w-7xl mx-auto px-2 lg:px-4 py-4 pb-16 lg:pb-20'>{children}</div>
        </div>
      </div>

      {/* Fixed Footer Navigation */}
      <div className='fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-t border-border shadow-xl z-50'>
        <div className='flex'>
          {/* Sidebar spacer */}
          {!isMobile && <div className='w-56'></div>}

          {/* Footer content aligned with main content */}
          <div className='flex-1'>
            <div className='max-w-7xl mx-auto px-2 lg:px-4 py-3'>
              <div className='flex justify-between items-center' data-tour='actions'>
                <Button
                  variant='outline'
                  onClick={handlePrevious}
                  disabled={currentIndex === 0}
                  className='flex items-center gap-2 h-8 px-4 border-border text-foreground hover:bg-primary/10 hover:border-primary/30 disabled:opacity-40 text-sm transition-all duration-300'
                >
                  <ChevronLeft className='w-3 h-3' />
                  <span className='hidden sm:inline'>{t('landing.exam.layout.previous')}</span>
                  <span className='sm:hidden'>{t('landing.exam.layout.previousShort')}</span>
                </Button>

                {currentIndex === questions.length - 1 ? (
                  <Button
                    onClick={handleSubmit}
                    className='flex items-center gap-2 h-8 px-4 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-white text-sm shadow-lg shadow-primary/25 transition-all duration-300'
                  >
                    <span className='hidden sm:inline'>
                      {t('landing.exam.layout.submit', { defaultValue: 'Nộp bài' })}
                    </span>
                    <span className='sm:hidden'>
                      {t('landing.exam.layout.submitShort', { defaultValue: 'Nộp' })}
                    </span>
                    <Send className='w-3 h-3 ml-1' />
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    className='flex items-center gap-2 h-8 px-4 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-white text-sm shadow-lg shadow-primary/25 transition-all duration-300'
                  >
                    <span className='hidden sm:inline'>{t('landing.exam.layout.next')}</span>
                    <span className='sm:hidden'>{t('landing.exam.layout.nextShort')}</span>
                    <ChevronRight className='w-3 h-3' />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Submit Confirmation Modal */}
      {showSubmitConfirm && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white dark:bg-gray-800 rounded-xl p-4 lg:p-6 max-w-md w-full mx-4 shadow-xl border border-border'>
            <div className='flex items-center gap-3 mb-4'>
              <AlertTriangle className='w-5 h-5 lg:w-6 lg:h-6 text-amber-500 flex-shrink-0' />
              <h3 className='text-base lg:text-lg font-semibold text-foreground'>
                {t('landing.exam.layout.confirmSubmit.title')}
              </h3>
            </div>

            <p className='text-sm lg:text-base text-muted-foreground mb-4'>
              {unansweredCount > 0
                ? t('landing.exam.layout.confirmSubmit.messageWithUnanswered', {
                    count: unansweredCount,
                    defaultValue: `Bạn còn ${unansweredCount} câu chưa trả lời. Bạn có chắc muốn nộp bài không?`,
                  })
                : t('landing.exam.layout.confirmSubmit.message', {
                    defaultValue:
                      'Bạn đã hoàn thành tất cả câu hỏi. Bạn có chắc muốn nộp bài không?',
                  })}
            </p>
            {unansweredCount > 0 && (
              <div className='bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-6'>
                <p className='text-sm text-amber-800 dark:text-amber-200'>
                  <strong>{unansweredCount}</strong>{' '}
                  {t('landing.exam.layout.confirmSubmit.unansweredWarning', {
                    count: unansweredCount,
                    defaultValue: 'câu hỏi chưa được trả lời sẽ được tính 0 điểm.',
                  })}
                </p>
              </div>
            )}

            <div className='flex gap-3 justify-end'>
              <Button
                variant='outline'
                onClick={() => setShowSubmitConfirm(false)}
                className='text-sm lg:text-base border-border hover:bg-primary/10 hover:border-primary/30 transition-all duration-300'
              >
                {t('landing.exam.layout.confirmSubmit.cancel')}
              </Button>
              <Button
                onClick={confirmSubmit}
                className='bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-white text-sm lg:text-base shadow-lg shadow-primary/25 transition-all duration-300'
              >
                {t('landing.exam.layout.confirmSubmit.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
